import React, { useEffect } from 'react';
import { ReactComponent as CopyIcon } from 'images/copy-icon.svg';
import { getPaymentAccountData } from 'selectors/paymentAccount';
import { getPaymentAccount } from 'thunks';
import { useSelector, useDispatch } from 'react-redux';

import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import { getApplicationData } from 'selectors/getApplicationData';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { copyToClipboard } from 'utils/copyToClipboard';
import MoreInformation from 'components/SetupDeduction/ManualDeduction/components/SetupViaPayroll/components/MoreInformation';
import { FlowComponentType } from 'routes/types';
import { usePartnerData } from 'enums/PartnerName';
import Loader from 'components/Loader';
import DecisionButtons, { DecisionOptions } from 'components/SetupDeduction/ManualDeduction/components/DecisionButtons';
import { RepaymentDecisionResult } from 'enums/FlowNextResults';
import { PayrollProviderName } from 'api/EmployersApi';

import styles from './SetupViaPayroll.module.scss';

const SetupViaPayroll = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const dispatch = useDispatch();
  const { directDepositInstructions } = usePartnerData();

  const {
    bankAccountData,
    status: {
      getPaymentAccount: { isLoading },
    },
  } = useSelector(getPaymentAccountData);

  const {
    id: applicationId,
    amountPerPaycheck,
    borrowerFirstName,
    borrowerLastName,
    fullDepositSwitch,
    employment,
  } = useSelector(getApplicationData).application!;

  const payrollProvider = employment.employer1?.payrollProvider ?? PayrollProviderName.Manual;
  const hideBackButton = !!(payrollProvider === PayrollProviderName.Manual && directDepositInstructions);
  const userStepAfterSignIn = employment.employer1?.employerPortalData?.userStep;

  const fullName = `${borrowerFirstName} ${borrowerLastName}`;
  const perPaycheckDeduction = formatMonetaryAmount(amountPerPaycheck, true);

  useEffect(() => {
    if (!bankAccountData) {
      dispatch(getPaymentAccount({ applicationId }));
    }
  }, [bankAccountData, applicationId]);

  const handleContinue = (decision: DecisionOptions) => {
    handleNext(decision === DecisionOptions.No ? RepaymentDecisionResult.No : RepaymentDecisionResult.Yes);
  };

  const subtitle = (() => {
    if (userStepAfterSignIn) return userStepAfterSignIn;

    return 'Look for a section related to payroll or payment methods.';
  })();

  return (
    <>
      <FormNavigation {...navigationInfo} showBackLink={!hideBackButton} />
      <FormContainer
        className={styles.formContainer}
        title={directDepositInstructions ? "Let's setup your payroll deduction." : 'Add new direct deposit account'}
        subtitle={directDepositInstructions ?? subtitle}
      >
        <div className={styles.accountInfoContainer}>
          <div className={styles.accountInfoTitle}>Use this information</div>
          <div className={styles.accountInfoContent}>
            <div className={styles.accountItem}>
              <div className={styles.accountItemName}>Account number:</div>
              {isLoading && <Loader color="#9d86f9" size={25} />}
              {bankAccountData && (
                <div className={styles.accountItemNumber}>
                  <span>{bankAccountData.accountNumber}</span>
                  <CopyIcon
                    className={styles.copyIcon}
                    onClick={() => copyToClipboard(bankAccountData.accountNumber)}
                  />
                </div>
              )}
            </div>
            <div className={styles.accountItem}>
              <div className={styles.accountItemName}>Routing number:</div>
              {isLoading && <Loader color="#9d86f9" size={25} />}
              {bankAccountData && (
                <div className={styles.accountItemNumber}>
                  <span>{bankAccountData.routingNumber}</span>
                  <CopyIcon
                    className={styles.copyIcon}
                    onClick={() => copyToClipboard(bankAccountData.routingNumber)}
                  />
                </div>
              )}
            </div>
            {!fullDepositSwitch && (
              <div className={styles.accountItem}>
                <div className={styles.accountItemName}>Per paycheck deduction:</div>
                <div className={styles.accountItemNumber}>
                  <span>{perPaycheckDeduction}</span>
                  <CopyIcon className={styles.copyIcon} onClick={() => copyToClipboard(perPaycheckDeduction)} />
                </div>
              </div>
            )}
          </div>

          <MoreInformation
            accountData={{
              employeeName: fullName,
              accountType: bankAccountData?.accountType,
              bankName: bankAccountData?.bankName,
            }}
          />
        </div>

        <DecisionButtons handleContinue={handleContinue} title="Are you able to add an account?" />
      </FormContainer>
    </>
  );
};

export default SetupViaPayroll;
